/**
 * Reviews V2 — API layer
 *
 * Talks to the ASP backend through the Shopify app proxy.
 * Config: window.__reviewsV2Config = { proxyBase, isLoggedIn, storeName }
 *
 * Endpoints:
 *   POST /v1/review/submit
 *   POST /v1/review/staged-upload
 *   POST /v1/review/file-create
 *   POST /v1/review/media-upload
 *
 * Reading reviews needs no API — the scheduler pushes approved reviews into
 * the spectrum.product_reviews metafield, and sorting/paging happens in JS.
 */
(function() {
  'use strict';

  if (window.__reviewsV2Init) return;
  window.__reviewsV2Init = true;

  // ═════════════════════════════════════════════════════════════════════════
  // 1. CONFIG
  // ═════════════════════════════════════════════════════════════════════════

  var config = window.__reviewsV2Config || {};

  function getProxyBase() {
    return (config.proxyBase || '/apps/asp/asp-shopify-service').replace(/\/+$/, '');
  }

  function getStoreName() {
    return config.storeName || '';
  }

  function isLoggedIn() {
    return config.isLoggedIn === true;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // 2. RESPONSE HELPERS
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * Unwrap the backend's AspGlobalResponse envelope:
   * { successful, errors, warnings, result, message } — payload is `result`.
   */
  function parseResponse(res) {
    return res.json().then(function(body) {
      if (res.ok && body.successful !== false) {
        return { ok: true, data: body.result !== undefined ? body.result : body };
      }
      return { ok: false, error: { message: extractErrorMessage(body), code: 'REQUEST_FAILED' } };
    });
  }

  function extractErrorMessage(body) {
    if (!body) return 'Request failed';
    if (typeof body.errors === 'string' && body.errors) return body.errors;
    if (Array.isArray(body.errors) && body.errors.length) {
      return body.errors.map(function(e) {
        return typeof e === 'string' ? e : (e.message || JSON.stringify(e));
      }).join(', ');
    }
    if (body.errors) return JSON.stringify(body.errors);
    if (body.message) return body.message;
    return 'Request failed';
  }

  function networkError(err) {
    return {
      ok: false,
      error: { message: err.message || 'Network error', code: 'NETWORK_ERROR' }
    };
  }

  // ═════════════════════════════════════════════════════════════════════════
  // 3. API — SUBMIT REVIEW
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * Submit a new review (status = WAITING_APPROVAL on backend).
   * @param {object} params
   * @param {string} params.shopifyProductId
   * @param {number} params.rating (1-5)
   * @param {string} [params.title]
   * @param {string} params.body (min 10 chars)
   * @param {string} params.authorDisplayName
   * @param {string} [params.customerEmail]
   * @param {string[]} [params.mediaFileGids] - GIDs from media upload step
   * @returns {Promise<{ok, data?: Review, error?}>}
   */
  function submitReview(params) {
    var url = getProxyBase() + '/v1/review/submit';

    var payload = {
      shopifyProductId: params.shopifyProductId,
      storeName: getStoreName(),
      rating: params.rating,
      title: params.title || '',
      body: params.body,
      authorDisplayName: params.authorDisplayName,
      customerEmail: params.customerEmail || '',
      source: 'storefront',
      mediaFileGids: params.mediaFileGids || []
    };

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(parseResponse).catch(networkError);
  }

  // ═════════════════════════════════════════════════════════════════════════
  // 5. API — MEDIA UPLOAD (3-step flow)
  // ═════════════════════════════════════════════════════════════════════════

  /**
   * Step 1: Request a staged upload target from Shopify (via our backend).
   * @param {File} file
   * @returns {Promise<{ok, data?: StagedTarget}>}
   *
   * StagedTarget shape: { url, resourceUrl, parameters: [{name, value}] }
   */
  function requestStagedUpload(file) {
    var url = getProxyBase() + '/v1/review/staged-upload';

    var resource = file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        resource: resource
      })
    }).then(parseResponse).catch(networkError);
  }

  /**
   * Step 2: Upload file bytes directly to Shopify's staged target.
   * @param {object} target - StagedTarget from step 1
   * @param {File} file
   * @returns {Promise<{ok}>}
   */
  function uploadToStagedTarget(target, file) {
    var formData = new FormData();

    // Signed parameters MUST be appended before the file field.
    if (target.parameters && target.parameters.length) {
      target.parameters.forEach(function(param) {
        formData.append(param.name, param.value);
      });
    }
    formData.append('file', file);

    return fetch(target.url, {
      method: 'POST',
      body: formData
    }).then(function(res) {
      // GCS targets answer 201 or 204 on success.
      return { ok: res.ok || res.status === 201 || res.status === 204, status: res.status };
    }).catch(function(err) {
      return { ok: false, status: 0, networkError: err.message };
    });
  }

  /**
   * Step 3a: Tell our backend to create a Shopify File from the staged upload.
   * @param {string} resourceUrl - from StagedTarget
   * @param {'IMAGE'|'VIDEO'} mediaKind
   * @param {string} [fileName] - used as the alt text
   * @returns {Promise<{ok, data?: ShopifyFile}>}
   */
  function createFile(resourceUrl, mediaKind, fileName) {
    var url = getProxyBase() + '/v1/review/file-create';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        resourceUrl: resourceUrl,
        alt: fileName || 'Review media',
        // Enum field name on MediaFileCreateRequest is unconfirmed; sibling DTOs
        // use `resource` / `mediaContentType`, Shopify uses `contentType`.
        // Unknown properties are ignored by Spring. Trim once the DTO is known.
        contentType: mediaKind,
        mediaContentType: mediaKind,
        resource: mediaKind
      })
    }).then(parseResponse).catch(networkError);
  }

  /**
   * Step 3b: Save media record in our DB (associates GID + URL with review later).
   * @param {object} params - { shopifyFileGid, url, mediaContentType }
   * @returns {Promise<{ok, data?: ShopifyProductReviewMedia}>}
   */
  function saveMediaRecord(params) {
    var url = getProxyBase() + '/v1/review/media-upload';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        shopifyFileGid: params.shopifyFileGid,
        url: params.url,
        mediaContentType: params.mediaContentType
      })
    }).then(parseResponse).catch(networkError);
  }

  /**
   * Pull a URL string out of a ShopifyFile. Right after fileCreate the file is
   * still processing, so `url` is often null and `preview` is an object — fall
   * back to the staged resourceUrl.
   */
  function resolveFileUrl(fileData, fallbackUrl) {
    if (!fileData) return fallbackUrl;
    if (typeof fileData.url === 'string' && fileData.url) return fileData.url;
    if (fileData.preview && fileData.preview.image && typeof fileData.preview.image.url === 'string') {
      return fileData.preview.image.url;
    }
    return fallbackUrl;
  }


  /**
   * Full upload flow for a single file:
   * staged-upload → upload to Shopify storage → file-create → media-upload
   *
   * @param {File} file
   * @returns {Promise<{ok, gid?: string, error?}>}
   */
  async function uploadFile(file) {
    var resource = file.type.indexOf('video/') === 0 ? 'VIDEO' : 'IMAGE';

    function fail(step, error) {
      var message = (error && error.message) || 'Unknown error';
      return { ok: false, error: { message: message, code: (error && error.code) || step } };
    }

    // Step 1 — ask the backend for a Shopify staged upload target
    var staged = await requestStagedUpload(file);
    if (!staged.ok) return fail('staged-upload', staged.error);

    var target = staged.data;
    if (!target || !target.url) {
      return fail('staged-upload', { message: 'No upload target returned', code: 'NO_TARGET' });
    }

    // Step 2 — post the bytes straight to Shopify's storage
    var uploaded = await uploadToStagedTarget(target, file);
    if (!uploaded.ok) {
      return fail('upload-to-storage', {
        message: 'Storage rejected the upload (status ' + uploaded.status + ')',
        code: 'UPLOAD_FAILED'
      });
    }

    // Step 3 — register the staged upload as a Shopify File
    var fileResult = await createFile(target.resourceUrl, resource, file.name);
    if (!fileResult.ok) return fail('file-create', fileResult.error);

    var fileGid = fileResult.data && (fileResult.data.id || fileResult.data.gid);
    if (typeof fileGid !== 'string' || !fileGid) {
      return fail('file-create', { message: 'No file GID returned', code: 'NO_FILE_GID' });
    }

    var fileUrl = resolveFileUrl(fileResult.data, target.resourceUrl);

    // Step 4 — persist the media row so createReview can attach it by GID
    var mediaResult = await saveMediaRecord({
      shopifyFileGid: fileGid,
      url: fileUrl,
      mediaContentType: resource
    });
    if (!mediaResult.ok) return fail('media-upload', mediaResult.error);

    return { ok: true, gid: fileGid };
  }

  /**
   * Upload multiple files in parallel.
   * All files are uploaded concurrently via Promise.all for faster throughput.
   * A rejected upload never breaks the batch — each result is captured per file.
   * @param {File[]} files
   * @returns {Promise<{ok, gids: string[], errors: string[]}>}
   */
  async function uploadFiles(files) {
    var outcomes = await Promise.all(
      files.map(async function(file) {
        try {
          return { file: file, result: await uploadFile(file) };
        } catch (err) {
          return {
            file: file,
            result: { ok: false, error: { message: err.message || 'Upload failed', code: 'UPLOAD_EXCEPTION' } }
          };
        }
      })
    );

    var gids = [];
    var errors = [];

    outcomes.forEach(function(outcome) {
      if (outcome.result.ok && outcome.result.gid) {
        gids.push(outcome.result.gid);
      } else {
        errors.push(outcome.file.name + ': ' + (outcome.result.error ? outcome.result.error.message : 'Failed'));
      }
    });

    return { ok: errors.length === 0, gids: gids, errors: errors };
  }

  // ═════════════════════════════════════════════════════════════════════════
  // 6. UI HELPERS
  // ═════════════════════════════════════════════════════════════════════════

  /** Generate star SVGs for a given rating */
  function starsHtml(rating, size) {
    size = size || 16;
    var html = '';
    var full = Math.round(rating) || 0;
    for (var i = 1; i <= 5; i++) {
      var fill = i <= full ? '#eab308' : '#e2e8f0';
      html += '<svg width="' + size + '" height="' + size + '" viewBox="0 0 16 16" aria-hidden="true">'
        + '<path d="M8 0l2.5 5.1 5.5.8-4 3.9.9 5.5L8 12.8l-4.9 2.5.9-5.5-4-3.9 5.5-.8z" fill="' + fill + '"/>'
        + '</svg>';
    }
    return html;
  }

  /** Escape HTML */
  function esc(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /** Format date string to readable format */
  function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════
  // 7. PUBLIC API
  // ═════════════════════════════════════════════════════════════════════════

  window.ReviewsV2 = {
    isLoggedIn: isLoggedIn,
    submitReview: submitReview,
    uploadFiles: uploadFiles,
    starsHtml: starsHtml,
    esc: esc,
    formatDate: formatDate
  };

})();
